// Making the map match the layouts. A compendium monster lands with HP on
// Bar 1 (and often AC on Bar 2); the DM's NPC layout may put them elsewhere.
// Remapping reads what each bar holds now, decides what it is, and writes
// the values (and links) into the bars the layout assigns — clearing the
// bars they came from — so what the DM Screen reads is what the map shows.
import type { BarLayout, BarNo, BarRole } from "./tokenBars";
import { BAR_NUMBERS } from "./tokenBars";

export interface RawBar { value: string; max: string; link: string }
export type RawBars = Partial<Record<`bar${BarNo}`, RawBar>>;
export interface BarWrite { value: string; max: string; link: string }
/** A remap decision: the writes to apply, and the moves REFUSED to avoid clobbering a
 *  bar whose value nothing else relocates (a DM's ad-hoc counter, or a real HP bar a
 *  misread temp would drop). `held` is what that bar holds now, so the DM can be told. */
export interface RemapPlan { writes: Partial<Record<`bar${BarNo}`, BarWrite>>; refused: { bar: BarNo; held: string }[] }

const num = (v: string | undefined) => { const m = String(v ?? "").trim().match(/^-?\d+(?:\.\d+)?/); return m ? Number(m[0]) : null; };
// A bar is OCCUPIED if it holds a non-empty value. "0" is occupied (a real zero — absent
// is not zero); "" is empty and free to write. What such a bar holds, for the DM's line.
const occupied = (b: RawBar | undefined) => String(b?.value ?? "").trim() !== "";
const held = (b: RawBar | undefined) => { const v = String(b?.value ?? "").trim(); const m = String(b?.max ?? "").trim(); return m ? `${v}/${m}` : v; };

/** What each bar of a token seems to hold, from its numbers alone. */
export function guessRoles(bars: RawBars): Partial<Record<BarNo, BarRole>> {
  const out: Partial<Record<BarNo, BarRole>> = {};
  const taken = new Set<BarRole>();
  const cells = BAR_NUMBERS.map((n) => ({ n, b: bars[`bar${n}`] })).filter((x) => x.b);
  // HP: value with a max, value ≤ max — the bar with the largest max (temp HP
  // sits on a small value = max bar), a linked bar winning ties.
  const hp = cells.filter(({ b }) => num(b!.value) !== null && num(b!.max) !== null && (num(b!.max) as number) > 0 && (num(b!.value) as number) <= (num(b!.max) as number)).sort((a, b) => (num(b.b!.max) as number) - (num(a.b!.max) as number) || Number(Boolean(b.b!.link)) - Number(Boolean(a.b!.link)) || a.n - b.n)[0];
  if (hp) { out[hp.n] = "hp"; taken.add("hp"); }
  // AC: a value with no max, 5–35, not the HP bar.
  const ac = cells.find(({ n, b }) => !out[n] && num(b!.value) !== null && num(b!.max) === null && (num(b!.value) as number) >= 5 && (num(b!.value) as number) <= 35);
  if (ac) { out[ac.n] = "ac"; taken.add("ac"); }
  // temp HP: value = max on a small bar, or a leftover value with no max.
  const temp = cells.find(({ n, b }) => !out[n] && num(b!.value) !== null && (num(b!.value) as number) > 0 && (num(b!.max) === num(b!.value) || num(b!.max) === null));
  if (temp) { out[temp.n] = "temp"; taken.add("temp"); }
  return out;
}

/** The full remap decision. Each classified bar wants to move to the layout's home for
 *  its role; a move is REFUSED when its destination holds a value that no OTHER move
 *  relocates — an unclassifiable counter, or a real HP bar a misread temp would drop. A
 *  refused move writes nothing (both bars are left exactly as they are), so a bar is only
 *  ever overwritten if it was empty or its own value is being carried elsewhere (a swap:
 *  the destination is itself a source). The refusal is returned, never silent. */
export function remapPlan(bars: RawBars, layout: BarLayout): RemapPlan {
  const roles = guessRoles(bars);
  // Planned moves, in bar order so a swap's clear-then-overwrite still lands right.
  const moves = BAR_NUMBERS
    .map((from) => ({ from, to: roles[from] ? BAR_NUMBERS.find((n) => layout[n] === roles[from]) : undefined }))
    .filter((m): m is { from: BarNo; to: BarNo } => m.to !== undefined && m.to !== m.from);
  const movingSources = new Set(moves.map((m) => m.from));
  const writes: Partial<Record<`bar${BarNo}`, BarWrite>> = {};
  const refused: { bar: BarNo; held: string }[] = [];
  for (const { from, to } of moves) {
    // The one thing this must never do: overwrite a value nothing else preserves.
    if (occupied(bars[`bar${to}`]) && !movingSources.has(to)) { refused.push({ bar: to, held: held(bars[`bar${to}`]) }); continue; }
    const src = bars[`bar${from}`]!;
    writes[`bar${to}`] = { value: src.value, max: src.max, link: src.link };
    if (!writes[`bar${from}`]) writes[`bar${from}`] = { value: "", max: "", link: "" };
  }
  return { writes, refused };
}

/** The writes that move a token's bars into the layout; null when nothing moves. Refused
 *  moves (see remapPlan) are already excluded, so this payload can never overwrite a bar
 *  it cannot safely replace. Callers that must TELL the DM a move was refused read remapPlan. */
export function remapWrites(bars: RawBars, layout: BarLayout): Partial<Record<`bar${BarNo}`, BarWrite>> | null {
  const { writes } = remapPlan(bars, layout);
  return Object.keys(writes).length ? writes : null;
}

export const describeRemap = (writes: Partial<Record<`bar${BarNo}`, BarWrite>>) => BAR_NUMBERS.filter((n) => writes[`bar${n}`]).map((n) => { const w = writes[`bar${n}`]!; return `bar ${n} ${w.value === "" && w.max === "" ? "cleared" : `= ${w.value}${w.max ? `/${w.max}` : ""}`}`; }).join(", ");

/** The DM-facing line for refused moves: which bars were left as is, and what they hold. */
export const describeRefused = (refused: { bar: BarNo; held: string }[]) =>
  refused.map((r) => `bar ${r.bar} left as is${r.held ? ` (held ${r.held})` : ""}`).join(", ");

// Console access for live diagnosis (harmless in production).
(globalThis as unknown as { __barRemap?: unknown }).__barRemap = { guessRoles, remapWrites, remapPlan };
